import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Spinner } from "react-bootstrap";

export default function CDSLData() {
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);

    // Keep track of which paths are expanded vs collapsed
    const [expandedPaths, setExpandedPaths] = useState({});

    // Handle user selecting a file -> entire chain: generate ID, fetch presigned URL, upload, fetch final API
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "application/pdf") {
            alert("Please select a valid PDF file.");
            return;
        }

        try {
            setLoading(true);
            setApiResponse(null);

            // 1) Generate a random ID
            const randomId = uuidv4();

            // 2) Fetch presigned URL
            const presignResponse = await fetch(
                `https://4ctqbasweb.execute-api.ap-south-1.amazonaws.com/default/presign_url_generate?unique_id=${randomId}&filetype=pdf`,
                { method: "POST" }
            );
            const presignData = await presignResponse.json();

            if (!presignData.URL) {
                alert("Failed to fetch upload URL");
                setLoading(false);
                return;
            }

            // 3) Upload PDF
            const uploadUrl = presignData.URL;
            const uploadResponse = await fetch(uploadUrl, {
                method: "PUT",
                body: file,
                headers: { "Content-Type": "application/pdf" },
            });
            if (!uploadResponse.ok) {
                alert("Upload failed.");
                setLoading(false);
                return;
            }
            console.log("File uploaded successfully");

            // 4) Call next API with that random ID
            const finalResponse = await fetch(
                `https://711bu2kxe7.execute-api.ap-south-1.amazonaws.com/default/cdsl_kra_api?file_key=${randomId}`,
                {
                    method: "POST",
                    headers: {
                        "x-api-key": "lxitg36TQn5EkrPL5OOCW5TfRLiIRaUU7swWDXnG",
                    },
                }
            );
            const finalData = await finalResponse.json();
            setApiResponse(finalData);
            console.log("Post-upload API Response:", finalData);
        } catch (error) {
            console.error("Error during file upload or API call:", error);
            alert("Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    // Toggle expanded/collapsed state for a given path
    const toggleExpand = (path) => {
        setExpandedPaths((prev) => ({
            ...prev,
            [path]: !prev[path],
        }));
    };

    /**
     * MAIN RENDER FUNCTION:
     * Decides if data is an array, object, or leaf and renders accordingly
     */
    const renderData = (data, parentKey = "") => {
        if (Array.isArray(data)) {
            // It's an array
            const isExpanded = expandedPaths[parentKey] || false;
            return (
                <div className="ms-3">
                    {parentKey && (
                        <div
                            className="d-flex align-items-center mb-1 fw-bold"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleExpand(parentKey)}
                        >
                            <span className="me-1">{isExpanded ? "▼" : "▶"}</span>
                            <span>
                                {parentKey} [{data.length}]
                            </span>
                        </div>
                    )}
                    {(!parentKey || isExpanded) && (
                        <ul className="list-unstyled ms-4">
                            {data.map((item, idx) => {
                                const newPath = parentKey
                                    ? `${parentKey}[${idx}]`
                                    : String(idx);
                                return (
                                    <li key={newPath} className="mb-2">
                                        {renderData(item, newPath)}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            );
        } else if (data && typeof data === "object") {
            // It's an object
            const isExpanded = expandedPaths[parentKey] || false;
            const keys = Object.keys(data);

            return (
                <div className="ms-2">
                    {/* Collapsible header if parentKey is present */}
                    {parentKey && (
                        <div
                            className="d-flex align-items-center mb-1 fw-bold"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleExpand(parentKey)}
                        >
                            <span className="me-1">{isExpanded ? "▼" : "▶"}</span>
                            <span>{parentKey}</span>
                        </div>
                    )}

                    {(!parentKey || isExpanded) && (
                        // TABLE for all key/value pairs in the object
                        <table className="table table-bordered table-striped table-sm ms-3">
                            <tbody>
                                {keys.map((key) => {
                                    const val = data[key];
                                    const newPath = parentKey
                                        ? `${parentKey}.${key}`
                                        : key;

                                    // If child is object/array, render nested inside table cell
                                    if (val && typeof val === "object") {
                                        return (
                                            <tr key={newPath}>
                                                <th style={{ width: "30%" }}>{key}</th>
                                                <td>{renderData(val, newPath)}</td>
                                            </tr>
                                        );
                                    } else {
                                        // Leaf field
                                        return (
                                            <tr key={newPath}>
                                                <th>{key}</th>
                                                <td>{renderLeafValue(val, newPath)}</td>
                                            </tr>
                                        );
                                    }
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            );
        } else {
            // It's a leaf (string/number/boolean/etc.)
            return <>{renderLeafValue(data, parentKey)}</>;
        }
    };

    /**
     * RENDER LEAF VALUE:
     * If a string looks like base64 for an image, show <img>, else text
     */
    const renderLeafValue = (value, path) => {
        if (typeof value === "string") {
            const trimmed = value.trim();
            const isJPEG = trimmed.startsWith("/9j/");
            const isPNG = trimmed.startsWith("iVBOR");

            if (isJPEG || isPNG) {
                const prefix = isJPEG
                    ? "data:image/jpeg;base64,"
                    : "data:image/png;base64,";
                const dataURI = prefix + trimmed;
                return (
                    <img
                        src={dataURI}
                        alt={path}
                        style={{ maxWidth: "200px" }}
                        className="border ms-2"
                    />
                );
            }
            // Otherwise, plain text
            return <span className="text-muted">{value}</span>;
        }
        // For numbers, booleans, null, etc.
        return <span className="text-muted">{JSON.stringify(value)}</span>;
    };

    return (
        <div className="container my-4">
            <h2 className="text-center mb-4">Upload KYC Data</h2>

            {/* 1. PDF File Selection */}
            <div className="row justify-content-center mb-4">
                <div className="col-md-4 text-center">
                    {loading ? (
                        <Spinner animation="border" />
                    ) : (
                        <input
                            type="file"
                            className="form-control"
                            accept=".pdf"
                            onChange={handleFileChange}
                        />
                    )}
                </div>
            </div>

            {/* 2. Collapsible JSON Tree, now using tables for objects */}
            {apiResponse && (
                <div className="mt-4">
                    <h4 className="mb-3">API Response (Table View)</h4>
                    {renderData(apiResponse)}
                </div>
            )}
        </div>
    );
}
